/* eslint-disable */
import { useEffect, useState } from "react";
import {
  Button,
  Flex,
  useColorModeValue,
  Stack,
  Select,
  useToast,
  Text,
  Box,
  SimpleGrid,
  Input,
} from "@chakra-ui/react";
import { MdChevronRight, MdChevronLeft } from "react-icons/md";
import dayjs from "dayjs";


import Card from "../../../../components/card/Card";
import { LoadingSpinner } from "../../../../constants/helper";
import { getCarrierOptions } from '../../carriers/hooks';
import { getAirlineOptions } from '../../airlines/hooks';
import { luggageCount } from '../hooks';

export default function LuggageCount() {
  const textColor = useColorModeValue("navy.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const brandColor = useColorModeValue("brand.500", "brand.400");
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    airlineId: "",
    carrierId: "",
  });

  const [carrierOptions, setCarrierOptions] = useState([]);
  const [airlineOptions, setAirlineOptions] = useState([]);

  useEffect(() => {
      const fetchOptions = async () => {
        setLoading(true);
          try {
              const response = await getCarrierOptions();
              setCarrierOptions(response.data);
              
              const airlineResponse = await getAirlineOptions();
              setAirlineOptions(airlineResponse.data);
          } catch (error) {
              console.error("Failed to fetch options:", error);
          } finally{
            setLoading(false);  
          }
      };
      fetchOptions();
  }, []);

  const [report, setReport] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = async () => {
    
    const { startDate, endDate } = filters;

    // Validation
    if (startDate && endDate && dayjs(endDate).isBefore(dayjs(startDate))) {
      toast({
        title: "Invalid Date Range",
        description: "End date must be greater than start date.",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setLoading(true);
    try {
      const res = await luggageCount(filters);
      if (res.success){
       const data = res.data;
        let carrierName = "";

        // Fetch carrier name if carrierId exists
        if (data?.carrierId) {
            carrierName = carrierOptions.find(
                (option) => option.value === data.carrierId
            )?.label || "";
        }

        // Format dates (if backend returns raw ones)
        const startDate = filters?.startDate
            ? dayjs(filters.startDate).format("YYYY-MM-DD")
            : null;
        const endDate = filters?.endDate
            ? dayjs(filters.endDate).format("YYYY-MM-DD")
            : null;

        // Final formatted report
        const formattedReport = {
            ...data,
            carrierName,
            startDate,
            endDate,
        };

        setReport(formattedReport);
      } else {
        toast({
          title: "Error",
          description: `${res?.data}`,
          status: "error", // could be success, error, or info
          duration: 5000, // time in ms before the toast disappears
          isClosable: true, // allows user to manually close the toast
          position: "top-right",
        });
      }

    } catch (err) {
      toast({
        title: "Error fetching report",
        description: err.message || "Something went wrong.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      
        <Flex direction="column" pt={{ sm: "125px", lg: "75px" }}>
          <Card px="22px" py="24px">
            <Text fontSize="xl" fontWeight="700" color={textColor} mb="6">
              Luggage Count Report
            </Text>

            {/* Filters */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
              <Input
                type="date"
                name="startDate"
                label="Start Date"
                value={filters.startDate}
                onChange={handleChange}
              />
              <Input
                type="date"
                name="endDate"
                label="End Date"
                value={filters.endDate}
                onChange={handleChange}
              />
              <Select
                placeholder="Select Airline (optional)"
                name="airlineId"
                value={filters.airlineId}
                onChange={handleChange}
              >
                {airlineOptions.map(option => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
              </Select>
              <Select
                placeholder="Select Carrier (optional)"
                name="carrierId"
                value={filters.carrierId}
                onChange={handleChange}
              >
                {carrierOptions.map(option => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
              </Select>
            </SimpleGrid>

            {/* Action Button */}
            <Flex justify="flex-end" mt="20px">
              <Button
                colorScheme="blue"
                onClick={handleSearch}
                rightIcon={<MdChevronRight />}
              >
                Get Report
              </Button>
            </Flex>
            {loading ? (
              <LoadingSpinner />
            ) : (
              report && (
                <Box
                    mt="30px"
                    p="20px"
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="16px"
                    bg={useColorModeValue("gray.50", "navy.700")}
                >
                    {/* Filters Info */}
                    <Stack mb="20px" spacing="6px">
                    {report?.airlineName && (
                        <Text fontWeight="500">Airline: {report.airlineName}</Text>
                    )}
                    {report?.carrierName && (
                        <Text fontWeight="500">Carrier: {report.carrierName}</Text>
                    )}

                    {/* ✅ Handle optional date filters */}
                    {report?.startDate && report?.endDate ? (
                        <Text fontWeight="500">
                        Date Range:{" "}
                        {dayjs(report.startDate).format("DD MMM YYYY")} →{" "}
                        {dayjs(report.endDate).format("DD MMM YYYY")}
                        </Text>
                    ) : report?.startDate ? (
                        <Text fontWeight="500">
                        From: {dayjs(report.startDate).format("DD MMM YYYY")}
                        </Text>
                    ) : report?.endDate ? (
                        <Text fontWeight="500">
                        Until: {dayjs(report.endDate).format("DD MMM YYYY")}
                        </Text>
                    ) : null}
                    </Stack>

                    {/* Main Title */}
                    <Text
                        fontSize="2xl"
                        fontWeight="700"
                        color={textColor}
                        mb="16px"
                        textAlign="left"
                        >
                        Luggage Counts
                    </Text>

                    {/* Status-wise Counts */}
                    <Stack spacing="6px" mt="10px" pl="10px">
                    <Text fontSize="md" fontWeight="600" color={textColor}>
                        Dispatched:{" "}
                        <Text as="span" color={brandColor} fontWeight="700">
                        {report?.counts?.Dispatched || 0}
                        </Text>
                    </Text>

                    <Text fontSize="md" fontWeight="600" color={textColor}>
                        In Transit:{" "}
                        <Text as="span" color={brandColor} fontWeight="700">
                        {report?.counts?.InTransit || 0}
                        </Text>
                    </Text>

                    <Text fontSize="md" fontWeight="600" color={textColor}>
                        Delivered:{" "}
                        <Text as="span" color={brandColor} fontWeight="700">
                        {report?.counts?.Delivered || 0}
                        </Text>
                    </Text>
                    </Stack>
                </Box>
              )
            )}

          </Card>
        </Flex>
    </>
  );
}
